/**
 * Guardian profile API (auth-service /api/guardians/me*, plan §5.3.5-§5.3.6).
 *
 * Responses carry snake_case and camelCase keys, so they can be used directly
 * as `Guardian`. Request bodies are snake_case.
 */
import { get, patch, post, API_ENDPOINTS } from './api';
import type { Guardian } from '../types/Guardian';

export interface GuardianProfileUpdate {
  first_name?: string;
  middle_name?: string;
  last_name?: string;
}

export interface EmailCodeSent {
  success: boolean;
  expiresInMinutes: number;
}

export const fetchMyProfile = async (): Promise<Guardian> =>
  (await get<Guardian>(API_ENDPOINTS.GUARDIAN.ME)) as unknown as Guardian;

export const updateMyProfile = async (body: GuardianProfileUpdate): Promise<Guardian> =>
  (await patch<Guardian>(API_ENDPOINTS.GUARDIAN.ME, body)) as unknown as Guardian;

export const requestEmailChange = async (email: string): Promise<EmailCodeSent> =>
  (await post<EmailCodeSent>(API_ENDPOINTS.GUARDIAN.EMAIL_REQUEST, { email })) as unknown as EmailCodeSent;

export const verifyEmailChange = async (email: string, code: string): Promise<Guardian> =>
  (await post<Guardian>(API_ENDPOINTS.GUARDIAN.EMAIL_VERIFY, { email, code })) as unknown as Guardian;

/** Stable backend error code (e.g. EMAIL_IN_USE) from an AppError, if any. */
export const apiErrorCode = (err: unknown): string | undefined => {
  const e = err as { details?: { data?: { code?: unknown }; status?: number } };
  const code = e?.details?.data?.code;
  if (typeof code === 'string') return code;
  if (e?.details?.status === 429) return 'RATE_LIMIT';
  if (e?.details?.status === 422) return 'VALIDATION_ERROR';
  return undefined;
};
